/**
 * Pure helpers for the shared JobCategoriesList (ER-11346, Organization > Job Categories).
 *
 * Kept framework-free so unit tests can exercise status mapping, the 4-Factor Score
 * gauge geometry and code formatting without mounting the SFC (mirrors the approach
 * used by existing shared table rows, e.g. PayPlansTable).
 */

/**
 * Fixed EIGE factor maxima (Settings — Jobs, Rule 1). Not account-configurable;
 * identical for every account.
 */
export const FACTOR_SCORE_KEYS = [
  {
    key: "s",
    label: "S",
    fullLabel: "Skill",
    definition:
      "The qualifications, knowledge, training, and experience required to perform the job.",
    max: 28,
  },
  {
    key: "e",
    label: "E",
    fullLabel: "Effort",
    definition: "The physical and mental exertion needed to carry out the job's tasks.",
    max: 15,
  },
  {
    key: "r",
    label: "R",
    fullLabel: "Responsibility",
    definition:
      "The degree of accountability for people, resources, decisions, or outcomes the job requires.",
    max: 20,
  },
  {
    key: "wc",
    label: "WC",
    fullLabel: "Working Conditions",
    definition:
      "The physical environment and conditions (e.g. hazards, discomfort) under which the job is performed.",
    max: 10,
  },
];

/**
 * Total Score maximum (Settings — Jobs, Rule 2): the straight sum of the four
 * factor maxima — 28 + 15 + 20 + 10 = 73. Derived from the per-factor maxima so
 * it can never drift from them (never written as the literal 73).
 */
export const TOTAL_SCORE_MAX = FACTOR_SCORE_KEYS.reduce((sum, factor) => sum + factor.max, 0);

export const QUICK_FILTERS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
];

const KNOWN_STATUSES = new Set(["active", "draft", "retired", "archived"]);

export function normalizeStatus(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return KNOWN_STATUSES.has(normalized) ? normalized : "active";
}

const GREYED_STATUSES = new Set(["retired", "archived"]);

export function isDisabledRow(status, disabled) {
  return Boolean(disabled) || GREYED_STATUSES.has(normalizeStatus(status));
}

export function displayCode(code) {
  const raw = String(code || "").trim();
  if (!raw) return "";
  return raw.startsWith("#") ? raw : `#${raw}`;
}

/** A score in points clamped to 0..max, or null when it is missing/invalid. */
function toPoints(value, max) {
  const numeric = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(numeric)) {
    return null;
  }
  return Math.min(max, Math.max(0, Math.round(numeric)));
}

export function normalizeFactorScores(factorScores) {
  const source = factorScores && typeof factorScores === "object" ? factorScores : {};
  return FACTOR_SCORE_KEYS.map(({ key, label, fullLabel, definition, max }) => {
    // The Job Category API returns each factor in points (ER-11935:
    // skills_score 0–28, effort 0–15, responsibility 0–20, working conditions
    // 0–10), the same unit the list displays ("Skills 19/28"). The gauge fill
    // width is the share of the factor's fixed maximum.
    const value = toPoints(source[key], max);
    return {
      key,
      label,
      fullLabel,
      definition,
      max,
      value,
      pct: value === null ? 0 : Math.round((value / max) * 100),
      display: value === null ? "–" : `${value}/${max}`,
    };
  });
}

export function normalizePositionCount(positionCount) {
  const numeric = Number(positionCount);
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0;
}

/**
 * True when the API row carries nested `scores` and every subfactor level is
 * null. The API reports an unscored category's factor scores and Total Score as
 * 0 points, which must not render as a real "0/28" score.
 */
function isUnscoredApiRow(raw) {
  const groups = raw.scores && typeof raw.scores === "object" ? Object.values(raw.scores) : [];
  const levels = groups.flatMap((group) =>
    group && typeof group === "object" ? Object.values(group) : []
  );
  return levels.length > 0 && levels.every((level) => level === null || level === undefined);
}

/**
 * Map a Job Category API row (ER-10771) onto the list's presentational shape.
 */
export function fromApiCategory(apiCategory) {
  const raw = apiCategory && typeof apiCategory === "object" ? apiCategory : {};
  const unscored = isUnscoredApiRow(raw);
  return {
    id: raw.id ?? null,
    name: String(raw.name || "").trim(),
    code: raw.code || "",
    status: raw.status,
    archived: Boolean(raw.archived),
    complianceStatus: String(raw.compliance_status || raw.complianceStatus || "").toLowerCase(),
    factorScores: unscored
      ? { s: null, e: null, r: null, wc: null }
      : {
          s: raw.skills_score ?? raw.factorScores?.s,
          e: raw.effort_score ?? raw.factorScores?.e,
          r: raw.responsibility_score ?? raw.factorScores?.r,
          wc: raw.working_conditions_score ?? raw.factorScores?.wc,
        },
    totalScore: unscored ? null : raw.total_score ?? raw.totalScore,
    // ER-11886 — the backend resolves the derived Value Band from the account's
    // band_count; the UI only ever shows its point range (no name or number).
    bandRange: unscored ? "" : String(raw.band_range ?? raw.bandRange ?? ""),
    positionCount: raw.position_count ?? raw.positionCount,
    unread: raw.unread,
    updatedOn: raw.updated_on || raw.updatedOn || "",
  };
}

export function needsReview(category) {
  const status = String(
    (category && (category.complianceStatus || category.compliance_status)) || ""
  ).toLowerCase();
  return status === "outdated";
}

const ACTION_META = {
  edit: { name: "Edit", icon: "icons/dropdown/edit.svg" },
  rename: { name: "Rename", icon: "icons/dropdown/rename.svg" },
  retire: { name: "Retire", icon: "icons/dropdown/activate.svg" },
  activate: { name: "Activate", icon: "icons/dropdown/activate.svg" },
  reactivate: { name: "Reactivate", icon: "icons/dropdown/activate.svg" },
  archive: { name: "Archive", icon: "icons/dropdown/archive-new.svg" },
  unarchive: { name: "Unarchive", icon: "icons/dropdown/unarchive.svg" },
  delete: {
    name: "Delete",
    icon: "icons/dropdown/trash.svg",
    destructive: true,
    negative: true,
  },
};

function menuItem(value) {
  return { value, ...ACTION_META[value] };
}

/** The Rename entry for menus that open the shared Rename dialog (ER-11901). */
export const RENAME_MENU_ITEM = menuItem("rename");

/**
 * MenuIcon items for a row (same shape as DepartmentLineMenu).
 * Draft → Activate + Delete-if-unassigned; Active → Retire; Retired →
 * Reactivate + Archive; Archived → Unarchive.
 */
export function rowActionsFor(row) {
  const view = row || {};
  const status = normalizeStatus(view.status);
  const archived = Boolean(view.archived);
  const unassigned = normalizePositionCount(view.positionCount) === 0;
  const actions = [menuItem("edit")];

  if (archived) {
    actions.push(menuItem("unarchive"));
    return actions;
  }

  if (status === "draft") {
    actions.push(menuItem("activate"));
    if (unassigned) actions.push(menuItem("delete"));
    return actions;
  }

  if (status === "active") {
    actions.push(menuItem("retire"));
    return actions;
  }

  if (status === "retired") {
    actions.push(menuItem("reactivate"));
    actions.push(menuItem("archive"));
    return actions;
  }

  return actions;
}

export function buildRowView(category) {
  const raw = category || {};
  const status = normalizeStatus(raw.status);
  const disabled = isDisabledRow(status, raw.disabled);
  const name = String(raw.name || "").trim();
  const positionCount = normalizePositionCount(raw.positionCount);
  const gauges = normalizeFactorScores(raw.factorScores);
  const totalScore = toPoints(raw.totalScore ?? raw.total_score, TOTAL_SCORE_MAX);
  const complianceStatus = String(raw.complianceStatus || raw.compliance_status || "").toLowerCase();

  const gaugeSummary = gauges
    .filter((gauge) => gauge.value !== null)
    .map((gauge) => `${gauge.fullLabel} ${gauge.value}`)
    .join(", ");

  return {
    id: raw.id ?? null,
    name,
    code: displayCode(raw.code),
    status,
    archived: Boolean(raw.archived),
    complianceStatus,
    needsReview: complianceStatus === "outdated",
    disabled,
    positionCount,
    gauges,
    gaugeSummary: gaugeSummary || "No factor scores",
    totalScore,
    totalScoreDisplay: totalScore === null ? "–" : String(totalScore),
    totalScoreMax: TOTAL_SCORE_MAX,
    unread: Number(raw.unread) > 0 ? Number(raw.unread) : 0,
    // Props consumed by the shared presentation components:
    cardState: disabled ? "Disabled" : "Active",
    badgeStatus: status,
    badgeNeutral: status === "retired" || status === "archived",
    actions: rowActionsFor({ status, archived: raw.archived, positionCount }),
  };
}

export function nextQuickStatus(currentStatus, targetStatus) {
  return currentStatus === targetStatus ? "" : targetStatus;
}
